import React, { useEffect } from "react";
import Slider from "react-slick";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";

import {
  fetchMostViewedPosts,
  fetchUserMostViewedPosts,
} from "../redux/slices/postSlice";

// ==========================
// API BASE URL
// ==========================
const API_URL = (
  process.env.REACT_APP_API_URL ||
  "https://api.bsangam.com"
)
  .replace(/\/uploads\/?$/, "")
  .replace(/\/$/, "");

// ==========================
// YOUTUBE THUMBNAIL
// ==========================
const getYoutubeThumbnail = (
  url?: string
) => {
  if (!url) {
    return "";
  }

  const match = url.match(
    /(?:youtube\.com\/watch\?v=|youtube\.com\/shorts\/|youtu\.be\/)([^&?/]+)/i
  );

  return match?.[1]
    ? `https://img.youtube.com/vi/${match[1]}/hqdefault.jpg`
    : "";
};

// ==========================
// UPLOAD URL HELPER
// ==========================
const getUploadUrl = (
  file?: string | null,
  youtubeUrl?: string
) => {
  if (
    file &&
    String(file).trim() !== ""
  ) {
    const value =
      String(file).trim();

    // Already full URL
    if (
      value.startsWith("http://") ||
      value.startsWith("https://")
    ) {
      return encodeURI(value);
    }

    // /uploads/file.jpg
    if (
      value.startsWith("/uploads/")
    ) {
      return encodeURI(
        `${API_URL}${value}`
      );
    }

    // uploads/file.jpg
    if (
      value.startsWith("uploads/")
    ) {
      return encodeURI(
        `${API_URL}/${value}`
      );
    }

    // /other/path/file.jpg
    if (
      value.startsWith("/")
    ) {
      return encodeURI(
        `${API_URL}${value}`
      );
    }

    // filename.jpg
    return encodeURI(
      `${API_URL}/uploads/${value}`
    );
  }

  // YouTube fallback
  if (youtubeUrl) {
    const thumbnail =
      getYoutubeThumbnail(
        youtubeUrl
      );

    if (thumbnail) {
      return thumbnail;
    }
  }

  return "/assets/images/story.png";
};

const Storyslider: React.FC = () => {
  const dispatch =
    useDispatch<any>();

  const navigate =
    useNavigate();

  // ==========================
  // REDUX DATA
  // ==========================
  const {
    mostViewedPosts = [],
  } = useSelector(
    (state: any) =>
      state.posts
  );

  const clientId =
    useSelector(
      (state: any) =>
        state.client?.client_id
    );

  // ==========================
  // FETCH MOST VIEWED POSTS
  // ==========================
  useEffect(() => {
    const loggedUser =
      JSON.parse(
        localStorage.getItem(
          "user"
        ) || "{}"
      );

    if (clientId) {
      dispatch(
        fetchMostViewedPosts(
          Number(clientId)
        )
      );

      return;
    }

    if (
      loggedUser?.user_id
    ) {
      dispatch(
        fetchUserMostViewedPosts(
          Number(
            loggedUser.user_id
          )
        )
      );
    }
  }, [
    dispatch,
    clientId,
  ]);

  // ==========================
  // SLIDER SETTINGS
  // ==========================
  const storySettings = {
    arrows: false,
    dots: false,
    infinite: false,
    speed: 300,
    slidesToShow: 1,
    slidesToScroll: 1,
    centerMode: false,
    variableWidth: true,
    swipeToSlide: true,
  };

  // ==========================
  // EMPTY
  // ==========================
  if (
    !Array.isArray(
      mostViewedPosts
    ) ||
    mostViewedPosts.length ===
      0
  ) {
    return null;
  }

  return (
    <>
      <style>
        {`
          .bs-story-header {
            display: flex;
            align-items: center;
            justify-content: space-between;
            margin-bottom: 12px;
          }

          .bs-story-title {
            margin: 0;
            color: #111827;
            font-size: 15px;
            font-weight: 800;
          }

          .bs-story-view {
            border: 0;
            background: transparent;
            color: #2563eb;
            font-size: 12px;
            font-weight: 800;
            cursor: pointer;
            padding: 0;
          }

          .bs-story-slider {
            width: 100%;
            overflow: hidden;
          }

          .bs-story-slider
          .slick-list {
            overflow: visible;
          }

          .bs-story-slider
          .slick-track {
            display: flex;
            align-items: stretch;
          }

          .bs-story-slider
          .slick-slide {
            height: auto;
          }

          .bs-story-slider
          .slick-slide > div {
            height: 100%;
          }

          .bs-story-card {
            width: 135px;
            height: 210px;
            border-radius: 20px;
            overflow: hidden;
            position: relative;
            margin-right: 14px;
            cursor: pointer;
            background: #eef2f7;
            box-shadow:
              0 10px 24px
              rgba(
                15,
                23,
                42,
                0.08
              );
            transition:
              all 0.22s ease;
          }

          .bs-story-card:hover {
            transform:
              translateY(-4px);

            box-shadow:
              0 16px 34px
              rgba(
                15,
                23,
                42,
                0.14
              );
          }

          .bs-story-img {
            width: 100%;
            height: 100%;
            object-fit: cover;
            display: block;
            transition:
              transform
              0.35s ease;
          }

          .bs-story-card:hover
          .bs-story-img {
            transform:
              scale(1.06);
          }

          .bs-story-overlay {
            position: absolute;
            inset: 0;

            background:
              linear-gradient(
                to top,
                rgba(
                    0,
                    0,
                    0,
                    0.78
                  )
                  0%,
                rgba(
                    0,
                    0,
                    0,
                    0.35
                  )
                  45%,
                rgba(
                    0,
                    0,
                    0,
                    0.02
                  )
                  100%
              );
          }

          .bs-story-top {
            position: absolute;
            top: 10px;
            right: 10px;
            z-index: 2;
          }

          .bs-story-views {
            color: #ffffff;
            font-size: 10px;
            font-weight: 800;

            text-shadow:
              0 2px 6px
              rgba(
                0,
                0,
                0,
                0.45
              );
          }

          .bs-story-avatar {
            width: 25px;
            height: 25px;

            border-radius: 50%;

            background:
              rgba(
                255,
                255,
                255,
                0.92
              );

            border:
              2px solid
              #ffffff;

            position:
              absolute;

            left: 10px;
            top: 12px;

            z-index: 2;

            display: flex;
            align-items: center;
            justify-content: center;

            color: #2563eb;

            font-size: 11px;
            font-weight: 900;
          }

          .bs-story-content {
            position:
              absolute;

            left: 10px;
            right: 10px;
            bottom: 11px;

            z-index: 2;
          }

          .bs-story-post-title {
            color: #ffffff;

            font-size:
              12px;

            font-weight:
              800;

            line-height:
              16px;

            margin:
              0 0 7px;

            overflow:
              hidden;

            display:
              -webkit-box;

            -webkit-line-clamp:
              2;

            -webkit-box-orient:
              vertical;

            text-shadow:
              0 2px 8px
              rgba(
                0,
                0,
                0,
                0.45
              );
          }

          .bs-story-client {
            color:
              rgba(
                255,
                255,
                255,
                0.82
              );

            font-size:
              10px;

            font-weight:
              700;

            display:
              block;

            white-space:
              nowrap;

            overflow:
              hidden;

            text-overflow:
              ellipsis;
          }

          @media (
            max-width: 768px
          ) {
            .bs-story-card {
              width: 120px;
              height: 190px;
              margin-right: 10px;
              border-radius: 17px;
            }

            .bs-story-post-title {
              font-size: 11px;
              line-height: 15px;
            }

            .bs-story-client {
              font-size: 9px;
            }
          }
        `}
      </style>

      <div className="bs-story-slider">
        <Slider
          {...storySettings}
        >
          {mostViewedPosts.map(
            (post: any) => {
              const title =
                post?.title ||
                post?.short_description ||
                "News";

              const clientName =
                post?.client_name ||
                "Community";

              const imageUrl =
                getUploadUrl(
                  post?.post_image,
                  post?.youtube_url
                );

              return (
                <div
                  key={
                    post?.post_id
                  }
                >
                  <div
                    className="bs-story-card"
                    onClick={() => {
                      if (
                        !post?.post_id
                      ) {
                        return;
                      }

                      navigate(
                        `/news-details/${post.post_id}`,
                        {
                          state: {
                            post,
                          },
                        }
                      );
                    }}
                  >
                    <img
                      src={
                        imageUrl
                      }
                      alt={
                        title
                      }
                      className="bs-story-img"
                      onError={(
                        e
                      ) => {
                        e.currentTarget.src =
                          "/assets/images/story.png";
                      }}
                    />

                    <div className="bs-story-overlay" />

                    <div className="bs-story-top">
                      <span className="bs-story-views">
                        👁{" "}
                        {post?.view_count ||
                          0}
                      </span>
                    </div>

                    <div className="bs-story-avatar">
                      {String(
                        clientName
                      )
                        .charAt(
                          0
                        )
                        .toUpperCase()}
                    </div>

                    <div className="bs-story-content">
                      <h4 className="bs-story-post-title">
                        {
                          title
                        }
                      </h4>

                      <span className="bs-story-client">
                        {
                          clientName
                        }
                      </span>
                    </div>
                  </div>
                </div>
              );
            }
          )}
        </Slider>
      </div>
    </>
  );
};

export default Storyslider;